import { IconLockFilled, IconPencil, IconTrash } from "@tabler/icons-react";
import { Badge, BotonExportarExcel, Group, IconAction, Stack, Switch, TablaSkeleton, Table, Tbody, Td, Text, Th, Thead, Tooltip, TooltipContent, TooltipTrigger, Tr, useConfirmDialog } from "@/components/ui";
import type { CategoriaDocumentoDto } from "../services/categorias-documento-service";
import { textoVigencia } from "@/features/admin-gestion-documental/configuracion-documental";
import { useCambiarEstadoCategoria, useEliminarCategoria } from "../hooks/use-categoria-documento-mutations";

/**
 * Tabla del catálogo de categorías de documento.
 *
 * Columnas: orden · código · nombre · extensiones · tamaño max · activa · acciones.
 * Sistema: badge "Sistema" + lock icon · sin botón eliminar.
 */
export function CategoriasDocumentoTable({
  cargando = false,
  categorias,
  onEditar,
  obtenerFilas,
}: {
  /** Primera carga (sin caché) · filas skeleton bajo el encabezado real. */
  cargando?: boolean;
  categorias: CategoriaDocumentoDto[];
  onEditar: (cat: CategoriaDocumentoDto) => void;
  /** Trae TODAS las páginas con la búsqueda actual · para el export. */
  obtenerFilas: () => Promise<CategoriaDocumentoDto[]>;
}) {
  return (
    <>
      <Group justify="flex-end" className="mb-2">
        <BotonExportarExcel
          archivo="categorias-documento"
          hoja="Categorías"
          obtenerFilas={obtenerFilas}
          columnas={[
            { header: "#", value: (c) => c.orden },
            { header: "Código", value: (c) => c.codigo },
            { header: "Nombre visible", value: (c) => c.nombre },
            { header: "Descripción", value: (c) => c.descripcion },
            { header: "Extensiones", value: (c) => c.extensiones.join(", ") },
            { header: "Tamaño máx (bytes)", value: (c) => c.tamanoMaximoBytes ?? "Límite global" },
            { header: "Exige tipo", value: (c) => (c.requiereTipo ? "Sí" : "No") },
            { header: "Requiere revisión", value: (c) => (c.configuracion.requiereRevision ? "Sí" : "No") },
            { header: "Roles revisores", value: (c) => (c.configuracion.rolesRevisores ?? []).join(", ") },
            { header: "Vigencia", value: (c) => textoVigencia(c.configuracion) },
            { header: "Sistema", value: (c) => (c.esSistema ? "Sí" : "No") },
            { header: "Activa", value: (c) => (c.activa ? "Sí" : "No") },
          ]}
        />
      </Group>
      <div className="overflow-x-auto">
        <Table verticalSpacing="sm" highlightOnHover>
          <Thead>
            <Tr>
              <Th className="w-14">#</Th>
              <Th>Código</Th>
              <Th>Nombre visible</Th>
              <Th>Extensiones permitidas</Th>
              <Th>Tamaño máx.</Th>
              <Th>Revisión · vigencia</Th>
              <Th>Activa</Th>
              <Th align="right" className="w-30">
                Acciones
              </Th>
            </Tr>
          </Thead>
          {cargando ? (
            <TablaSkeleton dentroDeTabla columnas={[{}, {}, {}, {}, {}, {}, {}, { align: "right" }]} filas={15} />
          ) : (
            <Tbody>
              {categorias.map((c) => (
                <FilaCategoria key={c.id} cat={c} onEditar={onEditar} />
              ))}
            </Tbody>
          )}
        </Table>
      </div>
    </>
  );
}

function FilaCategoria({ cat, onEditar }: { cat: CategoriaDocumentoDto; onEditar: (cat: CategoriaDocumentoDto) => void }) {
  const cambiarEstado = useCambiarEstadoCategoria();
  const eliminar = useEliminarCategoria();
  const confirm = useConfirmDialog();

  const confirmarEliminar = async () => {
    const ok = await confirm({
      title: "Eliminar categoría",
      message: `¿Eliminar la categoría "${cat.nombre}"? Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar",
      confirmColor: "peligro",
    });
    if (ok) eliminar.mutate(cat.id);
  };

  return (
    <Tr>
      <Td>
        <Text size="sm" c="dimmed">
          {cat.orden}
        </Text>
      </Td>
      <Td>
        <Group gap={6}>
          <Text size="sm" ff="mono">
            {cat.codigo}
          </Text>
          {cat.esSistema ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconLockFilled size={12} className="text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                {cat.esDelServidor
                  ? "Categoría de sistema · la registra el servidor al cargar la factura (extensiones fijas)"
                  : "Categoría de sistema · el código no cambia y no se elimina ni desactiva"}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </Group>
      </Td>
      <Td>
        <Stack gap={2}>
          <Text size="sm" fw={500}>
            {cat.nombre}
          </Text>
          {cat.descripcion ? (
            <Text size="xs" c="dimmed" truncate>
              {cat.descripcion}
            </Text>
          ) : null}
        </Stack>
      </Td>
      <Td>
        <Group gap={4} wrap="wrap">
          {cat.extensiones.map((ext) => (
            <Badge key={ext} size="xs" color="slate">
              {ext}
            </Badge>
          ))}
        </Group>
      </Td>
      <Td>
        <Text size="sm">{cat.tamanoMaximoBytes ? formatearTamano(cat.tamanoMaximoBytes) : "Límite global"}</Text>
      </Td>
      <Td>
        <Stack gap={2}>
          <Text size="xs">
            {cat.configuracion.requiereRevision ? `Revisión: ${(cat.configuracion.rolesRevisores ?? []).join(", ")}` : "Sin revisión"}
          </Text>
          <Text size="xs" c="dimmed">
            {textoVigencia(cat.configuracion)}
            {cat.requiereTipo ? " · exige tipo" : ""}
          </Text>
        </Stack>
      </Td>
      <Td>
        {/* Hotspot 82 · una categoría de sistema activa no se puede desactivar (el backend lo rechaza). */}
        <Switch
          checked={cat.activa}
          onCheckedChange={(v) => cambiarEstado.mutate({ id: cat.id, activa: v })}
          disabled={cambiarEstado.isPending || (cat.esSistema && cat.activa)}
          title={cat.esSistema && cat.activa ? "Las categorías de sistema no se desactivan" : undefined}
        />
      </Td>
      <Td align="right">
        <Group gap={4} justify="flex-end">
          <IconAction label={`Editar ${cat.nombre}`} variant="subtle" onClick={() => onEditar(cat)}>
            <IconPencil size={16} stroke={1.8} />
          </IconAction>
          <IconAction
            label={cat.esSistema ? "Las categorías de sistema no se eliminan" : `Eliminar ${cat.nombre}`}
            variant="subtle"
            iconColor="peligro-600"
            disabled={cat.esSistema || eliminar.isPending}
            onClick={confirmarEliminar}
          >
            <IconTrash size={16} stroke={1.8} />
          </IconAction>
        </Group>
      </Td>
    </Tr>
  );
}

function formatearTamano(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(0)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} KB`;
}
